<script setup lang="ts">
/**
 * NavMark — compact fanned euchre hand (♠ + Michigan home card + ♥) sized for a
 * nav / top bar. Ported from logos.jsx NavMark. `reversed` (default) tunes the
 * card stock for dark felt/paneling. The two side cards carry the
 * `nav-mark__card--fan` class so chrome can collapse to just the home card.
 */
import { computed } from 'vue'
import { BK } from './tokens'
import MichiganMitten from './MichiganMitten.vue'

const props = withDefaults(defineProps<{ h?: number; reversed?: boolean }>(), {
  h: 40,
  reversed: true,
})

const cw = computed(() => props.h * 0.7)
const cardH = computed(() => cw.value * 1.4)
const edge = computed(() => (props.reversed ? 'rgba(243,236,218,.16)' : 'rgba(43,37,25,.16)'))
const back = computed(() => (props.reversed ? '#eee3c9' : '#f4eddb'))
const front = '#f7f1e3'

const wrapStyle = computed(() => ({ width: `${cw.value * 1.86}px`, height: `${cardH.value}px` }))
const glyphSize = computed(() => `${cw.value * 0.6}px`)
const mittenSize = computed(() => cw.value * 0.62)

function cardStyle(tilt: number, zIndex: number, mitten = false) {
  return {
    zIndex,
    transform: `translateX(-50%) rotate(${tilt}deg)`,
    width: `${cw.value}px`,
    height: `${cardH.value}px`,
    borderRadius: `${cw.value * 0.12}px`,
    background: mitten ? front : back.value,
    border: `1.5px solid ${mitten ? BK.barn : edge.value}`,
  }
}
</script>

<template>
  <div class="nav-mark" :style="wrapStyle">
    <div class="nav-mark__card nav-mark__card--fan" :style="cardStyle(-19, 1)">
      <span class="nav-mark__glyph" :style="{ fontSize: glyphSize, color: BK.pine }">&spades;</span>
    </div>
    <div class="nav-mark__card nav-mark__card--fan" :style="cardStyle(19, 1)">
      <span class="nav-mark__glyph" :style="{ fontSize: glyphSize, color: BK.barn }">&hearts;</span>
    </div>
    <div class="nav-mark__card nav-mark__card--home" :style="cardStyle(0, 2, true)">
      <MichiganMitten :size="mittenSize" :fill="BK.pine" />
    </div>
  </div>
</template>

<style scoped>
.nav-mark {
  position: relative;
  flex-shrink: 0;
}
.nav-mark__card {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform-origin: bottom center;
  display: grid;
  place-items: center;
  box-shadow: 0 1px 0 rgba(43, 37, 25, 0.18), 0 4px 9px rgba(43, 37, 25, 0.28);
}
.nav-mark__glyph {
  font-family: "Zilla Slab", serif;
  line-height: 1;
}
</style>
